<template>
  <div v-if="allSections.length">
    <!-- options -->
    <div>
      <BaseButton
        :text="`Back to 'Your application'`"
        flat
        ripple
        class="font-weight-bold d-flex ml-n5"
        icon="mdi-chevron-left"
        color="#106175"
        small
        @click="
          $router.push({
            name: ROUTE_NAME.TENANTS_CONTENTS.B2B.APPLICATION,
          })
        "
      />

      <div class="d-flex justify-space-between mt-10">
        <h1 class="font-24 font-lg-28 font-weight-bold primary--text">
          Payment
        </h1>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </div>

      <v-alert color="var(--blue-100)" class="mt-7 rounded-lg">
        <div class="d-flex flex-no-wrap">
          <div class="mr-3">
            <v-icon color="var(--blue-500)">mdi-information</v-icon>
          </div>
          <div>
            <p class="mb-0">
              <span class="font-weight-bold">Note: </span>Tenant’s Content’s
              Insurance is a rolling monthly subscription and monthly payments
              are collected by Direct Debit. On your yearly policy anniversary
              date, Paymentshield will always do their best to offer the most
              suitable quote, whether that is from your current insurer or a
              different one, if they are cheaper. You’ll be contacted at least
              28 days before to let you know of any changes to your price or
              insurer.’
            </p>
          </div>
        </div>
      </v-alert>
    </div>

    <!-- the form -->
    <ValidationObserver ref="observerFormPaymentDetails" v-slot="{ validate }">
      <form @submit.prevent="onSubmit(validate)">
        <LayoutApplicationSummaryCard
          class="mt-12 mb-13"
          @showPremiumBreakdownMonthly="showPremiumBreakdownMonthlyCustomTitle"
          @showPremiumBreakdown3Months="showPremiumBreakdown3Months"
          @showPremiumBreakdownAnnual="showPremiumBreakdownAnnual"
          @showPremiumBreakdown2Months="showPremiumBreakdown2MonthsFree"
          @shareQuote="shareQuote"
          @printQuote="printQuote"
          :productGroup="productGroup"
          :showPayByCreditDebitCardMessage="false"
          :cardTitle="
            $vuetify.breakpoint.lgAndUp
              ? 'Your Tenant’s Contents Insurance quote'
              : 'Your quote'
          "
          :useRetrievedPaymentInfo="$route.params.type === 'resumeQuote'"
        />

        <BaseLabel
          text="What date do you want your monthly payment to be made?"
          class="mt-10 mb-2"
        />
        <BaseSelect
          :vid="directDebitDate.InterfaceKey"
          v-model="directDebitDate.Value"
          dataUiid="DirectDebitDate"
          :items="
            setItemDataUiids(
              'DirectDebitDate',
              DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL
            )
          "
          :isEnableSortOrderListItems="false"
          :rules="{
            required: {
              required: true,
              fieldLabel: 'Preferred Direct Debit date',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="What date do you want your monthly payment to be made?"
        />

        <v-alert
          color="var(--orange-100)"
          class="mt-4 mb-0 rounded-lg"
          v-if="directDebitDate.Value"
        >
          <div class="d-flex flex-no-wrap">
            <div class="mr-3">
              <v-icon color="var(--orange-500)">mdi-alert</v-icon>
            </div>
            <div>
              <span class="font-weight-bold">Important!</span> It can take up to
              ten working days to set up a Direct Debit which means your first
              payment may not be taken on your preferred date. The date of your
              first payment will be shown in your Welcome Letter. The rest of
              your payments will be taken on the date you choose from the
              options provided.
            </div>
          </div>
        </v-alert>

        <h2 class="font-20 font-weight-bold primary--text mt-9 mb-3">
          Payment details (Optional)
        </h2>

        <BaseAlert
          color="var(--blue-100)"
          icon="mdi-lock"
          alertText="Your payment is secure."
          alertTextClass="font-18 font-weight-bold primary--text"
          alertBorder
          alertBorderColor="var(--blue-500)"
          alertBorderPosition="left"
          prominent
          prominentColor="var(--blue-500)"
          prominentIconColor="white"
          prominentIconSize="19"
          rounded="0"
          :max-width="$vuetify.breakpoint.lgAndUp ? '571px' : '100%'"
        />

        <BaseLabel
          text="Account holder name"
          :hintText="'How does your name appear on your bank account?'"
          class="mt-10 mb-2"
        />
        <BaseInput
          :vid="bankAccountName.InterfaceKey"
          v-model="bankAccountName.Value"
          dataUiid="AccountHolderName"
          hideDetails="auto"
          :rules="{
            regex: {
              pattern: '^[a-zA-Z&\\s-]*$',
              customMessage:
                'Please enter only alpha characters, ampersands, spaces and hyphens in this field.',
            },
            maxLength: {
              length: 30,
              fieldLabel: 'Account holder name',
            },
          }"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '571px' : '100%',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Account holder name"
        />

        <div class="error-message-fit-input">
          <div class="d-block d-lg-inline-block">
            <BaseLabel text="Sort code" class="mt-10 mb-2" />
            <BaseInput
              :vid="bankSortCode.InterfaceKey"
              v-model="bankSortCode.Value"
              dataUiid="SortCode"
              hideDetails="auto"
              mask="##-##-##"
              :rules="{
                regex: {
                  pattern: SORT_CODE_REGEX,
                  customMessage: 'Please enter numerical values only.',
                },
                max: {
                  length: 8,
                  errorMessageTemplate: ERROR_MAX_LENGTH_SORT_CODE,
                  fieldLabel: 'Sort code',
                },
                min_length: {
                  length: 8,
                  errorMessageTemplate: ERROR_MIN_LENGTH_SORT_CODE,
                  fieldLabel: 'Sort code',
                },
              }"
              :style="{
                'max-width': $vuetify.breakpoint.lgAndUp ? '219px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Sort code"
            />
          </div>
          <div class="d-block d-lg-inline-block ml-lg-6 acc_no__wrapper">
            <BaseLabel text="Account number" class="mt-10 mb-2" />
            <BaseInput
              :vid="bankAccountNo.InterfaceKey"
              v-model="bankAccountNo.Value"
              dataUiid="AccountNumber"
              hideDetails="auto"
              :rules="{
                regex: {
                  pattern: '^[0-9]*$',
                  customMessage: 'Please enter numerical values only.',
                },
                min: {
                  length: 8,
                  fieldLabel: 'Account number',
                },
                maxLength: {
                  length: 8,
                  fieldLabel: 'Account number',
                },
              }"
              :style="{
                width: $vuetify.breakpoint.lgAndUp ? '328px' : '100%',
              }"
              :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
              aria-label="Account number"
            />
          </div>
        </div>

        <BaseButton
          depressed
          rounded
          dense
          class="font-weight-bold font-20 pa-7 px-13 mt-10 mb-3 primary--text"
          type="submit"
          icon="mdi mdi-lock"
          :loading="isApplying"
          text="SUBMIT"
          :color="'psorange'"
          :block="$vuetify.breakpoint.lgAndUp ? false : true"
        />
      </form>
    </ValidationObserver>

    <v-overlay :value="overlay">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- missing bank details dialog -->
    <BaseDialog
      ref="dialogDirectDebitMissingBankDetails"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-4 pb-5 mb-n1"
      headerDivider
      persistent
    >
      <template v-slot:content>
        <DialogContentMissingBankDetails
          @missingBankDetails="handleMissingBankDetails"
          @cancelMissingBankDetails="cancelMissingBankDetails"
          :sortCodeLength="bankSortCode.Value?.length"
          :accountNumberLength="bankAccountNo.Value?.length"
        />
      </template>
    </BaseDialog>

    <!-- premium breakdown dialog -->
    <BaseDialog
      ref="DialogContentPremiumBreakdown"
      windowClass="pa-0 pa-xl-4 pb-xl-0"
      headerClass="pa-4"
    >
      <template v-slot:content="{ data }">
        <DialogContentPremiumBreakdown
          :breakdownModel="data"
          :legalExpensesTitle="'Tenant’s legal expenses cover (ex IPT)'"
          :cancellingYourPolicyDescription="'You have the right to cancel your policy within the 14 day cooling off period. After the 14 day cooling off period, you will still have the right to cancel your policy, however you will be charged for the cost of your cover on a pro rata daily basis.'"
        ></DialogContentPremiumBreakdown>
      </template>
    </BaseDialog>

    <!-- share dialog -->
    <BaseDialog
      ref="shareDialog"
      headerIcon="mdi-share-variant"
      headerIconColor="#106175"
    >
      <template v-slot:content="{ data }">
        <DialogContentShareQuotes
          :content="getTenantsContentCover"
          :quoteId="data"
          :showSdn="isSdnCreated"
          :showSuitabilityReport="isSuitabilityReportCreated"
          @success="shareQuotesSuccess"
        ></DialogContentShareQuotes>
      </template>
    </BaseDialog>

    <!-- print dialog -->
    <BaseDialog
      ref="printQuotes"
      headerIcon="mdi-printer"
      headerIconColor="#106175"
      headerDivider
      windowClass="pa-0 pb-xl-0 mb-0"
      headerClass="px-7 pt-6 pb-3"
      contentClass="mb-0"
    >
      <template v-slot:content="{ data }">
        <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import { getErrorMessage, scrollTop } from "@/common/utils";
import { setItemDataUiids } from "@/constants/component.constants.js";
import {
  DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL,
  PAYMENT_FREQUENCY_ITEMS,
} from "@/constants/payment.constants.js";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapGetters, mapState } from "vuex";

// LayoutApplicationSummaryCard dependencies
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { getProductGroup } from "@/common/question-set";
import {
  ANNUAL,
  MONTHLY,
  MONTHLY_2_MONTHS_FREE,
  MONTHLY_3_MONTHS_DEFERRED,
} from "@/constants/common.constants";
import {
  ERROR_MAX_LENGTH_SORT_CODE,
  ERROR_MIN_LENGTH_SORT_CODE,
} from "@/constants/message.constants";
import { SORT_CODE_REGEX } from "@/constants/regex-patterns.constants";
import { ROUTE_NAME } from "@/constants/route.constants";

import BaseAlert from "@/components/BaseComponents/BaseAlert";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseDialog from "@/components/BaseComponents/BaseDialog";
import BaseInput from "@/components/BaseComponents/BaseInput";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseSelect from "@/components/BaseComponents/BaseSelect";

// LayoutApplicationSummaryCard components
import LayoutApplicationSummaryCard from "@/components/LayoutComponents/LayoutApplicationSummaryCard";
import DialogContentPremiumBreakdown from "@/components/QuoteResult/DialogContentPremiumBreakdown";
import DialogContentPrintQuotes from "@/components/QuoteResult/DialogContentPrintQuotes";
import DialogContentShareQuotes from "@/components/QuoteResult/DialogContentShareQuotes";
import DialogContentMissingBankDetails from "@/views/StageApplication/DialogContentMissingBankDetails";
export default {
  name: "PaymentPage",
  components: {
    ValidationObserver,
    BaseButton,
    BaseLabel,
    BaseSelect,
    BaseAlert,
    BaseInput,
    BaseDialog,
    LayoutApplicationSummaryCard,
    DialogContentMissingBankDetails,
    DialogContentPremiumBreakdown,
    DialogContentShareQuotes,
    DialogContentPrintQuotes,
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      setItemDataUiids,
      PAYMENT_FREQUENCY_ITEMS,
      DIRECT_DEBIT_DATE_ITEMS_TEXT_FULL,
      ANNUAL,
      MONTHLY,
      MONTHLY_3_MONTHS_DEFERRED,
      MONTHLY_2_MONTHS_FREE,
      ROUTE_NAME,
      SORT_CODE_REGEX,
      ERROR_MAX_LENGTH_SORT_CODE,
      ERROR_MIN_LENGTH_SORT_CODE,
      bankAccountName: null,
      bankSortCode: null,
      bankAccountNo: null,
      directDebitDate: null,

      // LayoutApplicationSummaryCard dependencies
      quoteDocuments: [],
      files: [
        {
          title: "Suitability report",
          key: "suitability",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Quote and Application Summary",
          key: "qas",
          isVisible: true,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Statement of Demands and Needs",
          key: "sdn",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
        {
          title: "Statement of Demands and Needs",
          key: "factfindsdn",
          isVisible: false,
          isDownloaded: false,
          isDownloading: false,
        },
      ],
      documentList: [],
      overlay: false,

      // Payment dependencies
      isApplying: false,
    };
  },
  computed: {
    ...mapGetters(APPLICATION_MODULE, ["allSections", "selectedQuote"]),
    ...mapGetters(QUESTIONS_MODULE, [
      "getTenantsContentCover",
      "getQuoteRequestId",
      "callChannel",
      "httpConfig",
      "insurerQuotesModel",
      "qsId",
      "branchNumber",
      "isAdvisedSeller",
    ]),
    ...mapState(APPLICATION_MODULE, ["paymentErrorMessage", "questionMap"]),
    productGroup() {
      return getProductGroup(this.qsId);
    },
    isSdnCreated() {
      // Quote docs response has returned and there is an SDN
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some(
          (f) => f.isVisible && (f.key === "sdn" || f.key === "factfindsdn")
        )
      );
    },
    isSuitabilityReportCreated() {
      // Quote docs response has returned and there is an Suitability Report
      return (
        this.quoteDocuments.length > 0 &&
        this.files.some((f) => f.isVisible && f.key === "suitability")
      );
    },
    isDirectDebitDetailsValid() {
      return !!this.bankSortCode.Value && !!this.bankAccountNo.Value;
    },
    premium() {
      const breakdown = this.buildPremiumBreakdownModel(
        this.selectedQuote,
        "DirectDebit",
        "Monthly3MonthsDeferred"
      );
      return breakdown.premium;
    },
  },
  created() {
    if (this.allSections.length) {
      this.bankAccountName = this.questionMap.BankAccountName;
      this.bankSortCode = this.questionMap.BankSortCode;
      this.bankAccountNo = this.questionMap.BankAccountNo;
      this.directDebitDate = this.questionMap.DirectDebitDate;
      if (!this.questionMap.PaymentFrequency.Value) {
        this.questionMap.PaymentFrequency.Value = MONTHLY;
      }
    }
  },
  mounted() {
    scrollTop();
    this.setRestoringLastStep(false);
    this.setCurrentRefObserverForm(this.$refs.observerFormPaymentDetails);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "toggleGlobalSaveAndExitDialog",
      "setRestoringLastStep",
      "getDataToPrintQuote",
      "getDocuments",
      "saveQuoteResults",
      "refreshSessionAndNextStep",
      "setCurrentRefObserverForm",
    ]),

    ...mapActions(APPLICATION_MODULE, [
      "setCardPaymentStatus",
      "handleApplicationSubmission",
      "setSessionStorageItems",
      "proceedToCardPayment",
      "setPaymentErrorMessage",
      "clearPaymentErrorMessage",
    ]),
    async onSubmit(validate) {
      const isFormValid = await validate();
      if (!isFormValid) {
        const el = this.$el.querySelector(".field-invalid:first-of-type");
        this.$vuetify.goTo(el, { offset: 50 });
        return;
      }

      if (!this.isDirectDebitDetailsValid) {
        this.$refs.dialogDirectDebitMissingBankDetails.open(
          "Missing bank details"
        );
      } else {
        this.submitForm();
      }
    },
    handleMissingBankDetails() {
      this.$refs.dialogDirectDebitMissingBankDetails.cancel();
      this.submitForm();
    },
    cancelMissingBankDetails() {
      this.$refs.dialogDirectDebitMissingBankDetails.cancel();
    },
    async shareQuote(quoteId) {
      await this.saveQuoteResults();

      this.$refs.shareDialog.open("Share via email", quoteId);
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    async saveQuotesAndGetDocumentsAsync() {
      await this.saveQuoteResults();

      return await this.getDocuments();
    },
    async printQuote(quote) {
      try {
        this.overlay = true;
        const docs = await this.saveQuotesAndGetDocumentsAsync();

        var data = await this.getDataToPrintQuote({
          docs,
          quote,
          paymentFrequency: this.questionMap.PaymentFrequency.Value,
        });
        this.$refs.printQuotes.open("Download for printing", data);
      } catch (error) {
        console.error(error);
      } finally {
        this.overlay = false;
      }
    },
    submitForm() {
      this.setCardPaymentStatus("");
      this.submitDirectDebitApplication();
    },
    async submitDirectDebitApplication() {
      this.isApplying = true;

      try {
        await this.handleApplicationSubmission();
        this.isApplying = false;
        this.setSessionStorageItems();
        this.clearPaymentErrorMessage();
        this.refreshSessionAndNextStep(
          ROUTE_NAME.TENANTS_CONTENTS.B2B.COMPLETE
        );
      } catch (error) {
        this.isApplying = false;
        this.setPaymentErrorMessage(getErrorMessage(error));
        console.log("Apply error:", error);
      }
    },
    showPremiumBreakdownMonthlyCustomTitle(quote, productGroup) {
      const customTitle = "Monthly Premium Breakdown";
      this.showPremiumBreakdownMonthly(quote, productGroup, customTitle);
    },
  },
};
</script>

<style lang="scss" scoped>
.acc_no__wrapper {
  min-width: 328px;
}
:deep(.v-text-field__details) {
  margin-bottom: 0 !important;
}
</style>
